import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { OneOfElementListElements } from '@shared/models/project.model';
import { useSelectedVersion } from '@client/store/ui';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { useProposals } from '@features/proposals/hooks/use-proposals.hooks';
import {
  ELEMENT_CO2_BAR_HEIGHT,
  ELEMENT_COST_BAR_HEIGHT,
  MAX_VISIBLE_PROPOSALS,
} from '@ui/ElementList';
import {
  useElementResults,
  useElementMaxResults,
  useGetResultsPerGFA,
} from '@features/results/use-results.hooks';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { filterObject, getId } from '@shared/helpers/object.helpers';
import { isCostUnit } from '@shared/helpers/unit.helpers';
import { SimpleBarChart } from '@ui/BarChart';
import { ElementProposalBarChart } from './ElementProposalBarChart';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useCo2eVisible } from '@features/results/co2e-visible.hook';
import { Results } from '@shared/models/unit.model';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';

type ElementBarChartsProps = {
  element: OneOfElementListElements;
  width?: number | string;
  dimmed?: boolean;
};

/**
 * Proposal-aware element bar charts.
 * Shows baseline when no proposal is selected, or per-proposal bars for selected proposals.
 * Uses {@link ElementProposalBarChart} for both baseline and proposal rendering.
 */
export const ElementBarCharts: React.FC<ElementBarChartsProps> = ({
  element,
  width = '100%',
  dimmed,
}) => {
  const { classes, cx } = useStyles();
  const displayCurrency = useDisplayCurrency();
  const co2eVisible = useCo2eVisible();
  const getResultsPerGFA = useGetResultsPerGFA();

  const selectedProposalId = useSelectedProposalId();
  const allProposals = useProposals();

  // Resolve which proposals to display (supports future multi-select)
  const selectedProposals = useMemo(() => {
    if (!selectedProposalId) return [];
    return allProposals
      .filter((p) => p.id === selectedProposalId)
      .slice(0, MAX_VISIBLE_PROPOSALS);
  }, [selectedProposalId, allProposals]);

  const version = useSelectedVersion();

  // Always read baseline results from version.resultsRecord (unaffected by proposal selection)
  const elementId = getId(element);
  const baselineResults: Results = useMemo(
    () => version?.resultsRecord?.[elementId] ?? {},
    [version?.resultsRecord, elementId],
  );

  const maxResults = useElementMaxResults();
  const maxResultsPerGFA = getResultsPerGFA(maxResults);
  const baselineResultsPerGFA = getResultsPerGFA(baselineResults);
  const elementResults = useElementResults(element);

  const co2ePerGFA = getConversionFactorValue(baselineResultsPerGFA, 'co2e');
  const co2ePerGfaMax = getConversionFactorValue(maxResultsPerGFA, 'co2e');

  const costResultsPerGFA =
    selectedProposals.length > 0
      ? getResultsPerGFA(elementResults)
      : baselineResultsPerGFA;
  const sekFactorsPerGFA = useMemo(
    () => filterObject(costResultsPerGFA, (value, key) => isCostUnit(key)),
    [costResultsPerGFA],
  );
  const sekPerGfaMax = getConversionFactorValue(maxResultsPerGFA, 'sek');

  return (
    <Box
      className={cx(classes.container, dimmed && classes.dimmed)}
      sx={{
        width: width,
      }}
    >
      {co2eVisible.isEnabled && (
        <>
          {/* Per-proposal bars (selected proposals) */}
          {selectedProposals.map((proposal) => (
            <ElementProposalBarChart
              key={proposal.id}
              proposal={proposal}
              element={element}
              maxValue={co2ePerGfaMax}
            />
          ))}

          {/* Baseline bar when no proposal is selected */}
          {selectedProposals.length === 0 && (
            <Box className={classes.chart}>
              <SimpleBarChart
                value={co2ePerGFA}
                maxValue={co2ePerGfaMax}
                color={BASELINE_COLOR}
                height={ELEMENT_CO2_BAR_HEIGHT}
              />
            </Box>
          )}
        </>
      )}

      {/* Cost */}
      <Box className={classes.chart}>
        {displayCurrency.isEnabled && (
          <SimpleBarChart
            height={ELEMENT_COST_BAR_HEIGHT}
            value={sekFactorsPerGFA}
            maxValue={sekPerGfaMax}
            color="black"
          />
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  container: {
    position: 'relative',
    display: 'block',
    height: 'auto',
  },
  dimmed: {
    opacity: 0.5,
  },
  chart: {
    width: '100%',
    '&:not(:last-child)': {
      marginBottom: '2px',
    },
  },
}));

export default ElementBarCharts;
